import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  HallOption,
  checkHallAmount,
  createHallBooking,
  fetchMasterOptions,
  getLoggedInEmpId,
  todayYmd,
  toDotFormat,
} from "./communityHall.service";

const PAGE_TITLE = "Booking of Community Hall/Common Room/Park";
const ESS_NOTE =
  "Note - Booking of community hall / common room / open park, shall be initiated on ESS 05 working days prior to the date for which booking is required. Bookings request shall be sent through ESS only. Hard copy requests shall not be entertained.";

type FormErrors = {
  address?: string;
  contactNumber?: string;
  bookingBeginDate?: string;
  bookingEndDate?: string;
  purpose?: string;
  requestType?: string;
  staffQuarter?: string;
};

const CreateCommunityHallBooking: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const label = (key: string, fallback: string) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [address, setAddress] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [purpose, setPurpose] = useState("");
  const [beginDate, setBeginDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [requestTypes, setRequestTypes] = useState<HallOption[]>([]);
  const [staffQuarters, setStaffQuarters] = useState<HallOption[]>([]);
  const [requestType, setRequestType] = useState("");
  const [staffQuarter, setStaffQuarter] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [amount, setAmount] = useState({ BookingAmount: "0.00", GstAmount: "0.00", TotalAmount: "0.00", TotalDays: "0.00" });
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [thankYouOpen, setThankYouOpen] = useState(false);

  useEffect(() => {
    setLoadingOptions(true);
    fetchMasterOptions()
      .then((options) => {
        setRequestTypes(options.requestTypes);
        setStaffQuarters(options.staffQuarters);
      })
      .catch((err: any) => {
        toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
      })
      .finally(() => setLoadingOptions(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const required = label("this_field_is_required", "This field is required");

  const validate = () => {
    const next: FormErrors = {};
    if (!address.trim()) next.address = required;
    if (!contactNumber) next.contactNumber = required;
    else if (contactNumber.length !== 10) {
      next.contactNumber = label("please_provide_valid_10_digit_mobile_number", "Please provide a valid 10 digit mobile number");
    }
    if (!beginDate) next.bookingBeginDate = required;
    if (!endDate) next.bookingEndDate = required;
    if (!purpose.trim()) next.purpose = required;
    if (!requestType) next.requestType = required;
    if (!staffQuarter) next.staffQuarter = required;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = () => ({
    EmpID: getLoggedInEmpId(),
    RequestType: requestType,
    StaffQuarter: staffQuarter,
    BookingBeginDate: toDotFormat(beginDate),
    BookingEndDate: toDotFormat(endDate),
    Purpose: purpose,
    ResidentialAddressOfEmployee: address,
    ContactNumber: contactNumber,
  });

  const handleCheckAmount = async () => {
    if (!beginDate || !endDate || !requestType) {
      toast.error(label("fill_request_type_dates_error", "Please fill Request Type and both dates before checking the amount"));
      return;
    }
    setChecking(true);
    try {
      const result = await checkHallAmount({
        EmpID: getLoggedInEmpId(),
        BookingBeginDate: toDotFormat(beginDate),
        BookingEndDate: toDotFormat(endDate),
        RequestType: requestType,
      });
      if (!result.ok || !result.data) {
        setAmount({ BookingAmount: "0.00", GstAmount: "0.00", TotalAmount: "0.00", TotalDays: "0.00" });
        toast.error(result.message || label("something_went_wrong", "Something went wrong"));
        return;
      }
      setAmount(result.data);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setChecking(false);
    }
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const result = await createHallBooking({ Action: "C", ReferenceNumber: "", ...buildPayload() });
      if (result.ok) setThankYouOpen(true);
      else toast.error(result.message || label("something_went_wrong", "Something went wrong"));
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const busy = loadingOptions || checking || submitting;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{label("booking_of_community_hall_common_room_park", PAGE_TITLE)}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.adminDashboard}>Dashboard</Link></li>
                <li className="breadcrumb-item">
                  <Link to={routes.communityHall.list}>{label("booking_of_community_hall_common_room_park", PAGE_TITLE)}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">{t("create_new_request")}</li>
              </ol>
            </nav>
          </div>
          <button type="button" className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left me-1"></i>
            {label("back", "Back")}
          </button>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loadingOptions && (
              <div className="text-center py-3">
                <div className="spinner-border text-danger" role="status" />
              </div>
            )}
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-semibold">{label("residential_address_employee_colon", "Residential address of Employee :")}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setErrors((prev) => ({ ...prev, address: "" }));
                  }}
                />
                {errors.address && <div className="text-danger small mt-1">{errors.address}</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{t("mobile_no")} <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  inputMode="numeric"
                  maxLength={10}
                  value={contactNumber}
                  onChange={(e) => {
                    const numeric = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                    setContactNumber(numeric);
                    setErrors((prev) => ({ ...prev, contactNumber: "" }));
                  }}
                />
                {errors.contactNumber && <div className="text-danger small mt-1">{errors.contactNumber}</div>}
              </div>
              <div className="col-md-6" />
              <div className="col-md-6">
                <label className="form-label fw-semibold">{label("booking_begin_date", "Booking Begin Date")} <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  min={todayYmd()}
                  value={beginDate}
                  onChange={(e) => {
                    setBeginDate(e.target.value);
                    setErrors((prev) => ({ ...prev, bookingBeginDate: "" }));
                  }}
                />
                {errors.bookingBeginDate && <div className="text-danger small mt-1">{errors.bookingBeginDate}</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{label("booking_end_date", "Booking End Date")} <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  min={beginDate || todayYmd()}
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setErrors((prev) => ({ ...prev, bookingEndDate: "" }));
                  }}
                />
                {errors.bookingEndDate && <div className="text-danger small mt-1">{errors.bookingEndDate}</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{label("booking_amount", "Booking Amount")}</label>
                <input className="form-control bg-light" value={amount.BookingAmount} readOnly />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{label("gst_amount", "GST amount")}</label>
                <input className="form-control bg-light" value={amount.GstAmount} readOnly />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{label("total_amount", "Total amount")}</label>
                <input className="form-control bg-light" value={amount.TotalAmount} readOnly />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{label("total_days_of_booking", "Total days of booking")}</label>
                <input className="form-control bg-light" value={amount.TotalDays} readOnly />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{label("request_type", "Request Type")} <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={requestType}
                  onChange={(e) => {
                    setRequestType(e.target.value);
                    setErrors((prev) => ({ ...prev, requestType: "" }));
                  }}
                >
                  <option value="">{label("select_option", "Select Option")}</option>
                  {requestTypes.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {errors.requestType && <div className="text-danger small mt-1">{errors.requestType}</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{label("staff_quarter", "Staff Quarters")} <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={staffQuarter}
                  onChange={(e) => {
                    setStaffQuarter(e.target.value);
                    setErrors((prev) => ({ ...prev, staffQuarter: "" }));
                  }}
                >
                  <option value="">{label("select_option", "Select Option")}</option>
                  {staffQuarters.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {errors.staffQuarter && <div className="text-danger small mt-1">{errors.staffQuarter}</div>}
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{t("purpose")}</label>
                <input
                  className="form-control"
                  value={purpose}
                  onChange={(e) => {
                    setPurpose(e.target.value);
                    setErrors((prev) => ({ ...prev, purpose: "" }));
                  }}
                />
                {errors.purpose && <div className="text-danger small mt-1">{errors.purpose}</div>}
              </div>
              <div className="col-12">
                <div className="rounded p-2" style={{ background: "#E7DCEF" }}>
                  {label(
                    "booking_cancellation_warning",
                    "Please note that Cancellation Request for this Booking will not be possible until the booking request has been Approved."
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="rounded p-2" style={{ background: "#D3E4E3" }}>
                  {ESS_NOTE}
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button type="button" className="btn btn-primary" disabled={busy} onClick={handleCheckAmount}>
                {checking ? label("loading", "Loading...") : label("check_amount", "Check Amount")}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy}
                onClick={() => {
                  if (!validate()) return;
                  (document.activeElement as HTMLElement | null)?.blur();
                  setConfirmOpen(true);
                }}
              >
                {label("submit", "Submit")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {confirmOpen &&
        createPortal(
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 10050 }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content border-0 shadow">
                <div className="modal-body text-center p-4">
                  <div className="mb-3">
                    <span
                      className="avatar avatar-xl rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: 60, height: 60, backgroundColor: "#fff4e5", color: "#ff9800" }}
                    >
                      <i className="ti ti-alert-circle fs-28" />
                    </span>
                  </div>
                  <h4 className="mb-2">{label("confirm_submission", "Confirm Submission")}</h4>
                  <p className="text-muted mb-4">
                    {label("are_you_sure_you_want_to_submit", "Are you sure you want to Submit?")}
                  </p>
                  <div className="d-flex justify-content-center gap-2">
                    <button type="button" className="btn btn-outline-secondary px-4" onClick={() => setConfirmOpen(false)}>
                      {label("cancel", "Cancel")}
                    </button>
                    <button type="button" className="btn btn-danger px-4" onClick={handleConfirmSubmit}>
                      {label("submit", "Submit")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {thankYouOpen &&
        createPortal(
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 10050 }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content border-0 shadow">
                <div className="modal-body text-center p-4">
                  <i className="ti ti-circle-check text-success fs-1"></i>
                  <p className="mt-3 mb-4">{label("request_submitted_successfully", "Request Submitted Successfully.")}</p>
                  <button type="button" className="btn btn-danger px-4" onClick={() => navigate(routes.communityHall.list, { replace: true })}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CreateCommunityHallBooking;
